import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Mic, Square, Sparkles, FileText, Volume2, VolumeX } from "lucide-react";
import api from "@/api/client";
import AIStarfield from "@/components/AIStarfield";

const T = {
  en: {
    badge: "Whisper AI",
    tapToSpeak: "Tap to speak",
    recording: (s) => `Recording… ${s}s`,
    transcribing: "Transcribing…",
    thinking: "Thinking…",
    speaking: "Speaking…",
    hintIdle: (name) => `Tap the microphone to ask about ${name}`,
    hintRecording: "Speak now, tap to stop recording",
    you: "You",
    assistant: "Assistant",
    sources: "Sources",
    micDenied: "Microphone access was blocked. Please allow it and try again.",
    micUnavailable: "Microphone is not available in this browser.",
    didntCatch: "I didn't catch that. Tap and try again.",
    speakToggleOn: "Voice replies on",
    speakToggleOff: "Voice replies off",
  },
  ar: {
    badge: "Whisper AI",
    tapToSpeak: "اضغط للتحدث",
    recording: (s) => `جارٍ التسجيل… ${s} ث`,
    transcribing: "جارٍ التحويل…",
    thinking: "جارٍ التفكير…",
    speaking: "جارٍ التحدث…",
    hintIdle: (name) => `اضغط الميكروفون لتسأل عن ${name}`,
    hintRecording: "تحدث الآن، اضغط لإيقاف التسجيل",
    you: "أنت",
    assistant: "المساعد",
    sources: "المصادر",
    micDenied: "تم رفض الوصول للميكروفون. يرجى السماح والمحاولة مجدداً.",
    micUnavailable: "الميكروفون غير متاح في هذا المتصفح.",
    didntCatch: "لم أسمع شيئاً واضحاً. حاول مرة أخرى.",
    speakToggleOn: "الإجابات الصوتية مفعّلة",
    speakToggleOff: "الإجابات الصوتية موقوفة",
  },
};

const STAGE_IDLE = "idle";
const STAGE_RECORDING = "recording";
const STAGE_TRANSCRIBING = "transcribing";
const STAGE_THINKING = "thinking";
const STAGE_SPEAKING = "speaking";

export default function VoiceAIPanel({ open, onClose, spaceSlug, spaceName, lang = "en", primary = "#41B5E6", theme = "dark" }) {
  const t = T[lang] || T.en;

  const [stage, setStage] = useState(STAGE_IDLE);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  const [transcript, setTranscript] = useState("");
  const [answer, setAnswer] = useState("");
  const [sources, setSources] = useState([]);
  const [ttsOn, setTtsOn] = useState(() => localStorage.getItem("voiceai_tts") !== "0");

  // Refs for audio capture + visualisation
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(0);
  const canvasRef = useRef(null);
  const timerRef = useRef(0);

  useEffect(() => {
    localStorage.setItem("voiceai_tts", ttsOn ? "1" : "0");
  }, [ttsOn]);

  // Cleanup on unmount / close
  useEffect(() => {
    if (open) {
      const iframe = document.querySelector('iframe[title="Handle AI chat"]');
      if (iframe) iframe.style.display = "none";
    } else {
      stopAll(true);
      const iframe = document.querySelector('iframe[title="Handle AI chat"]');
      if (iframe) iframe.style.display = "";
    }
    return () => stopAll(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopAll = (resetUi = false) => {
    cancelAnimationFrame(rafRef.current);
    clearInterval(timerRef.current);
    try { mediaRecorderRef.current?.state === "recording" && mediaRecorderRef.current.stop(); } catch { /* noop */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { audioCtxRef.current?.close(); } catch { /* noop */ }
    streamRef.current = null;
    audioCtxRef.current = null;
    analyserRef.current = null;
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    if (resetUi) {
      setStage(STAGE_IDLE);
      setSeconds(0);
    }
  };

  const startRecording = async () => {
    setError("");
    setTranscript("");
    setAnswer("");
    setSources([]);
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError(t.micUnavailable);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      recorder.onstop = handleStop;
      recorder.start();

      // Visualiser
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      analyserRef.current = analyser;
      drawWaveform();

      setStage(STAGE_RECORDING);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);

      // Auto-stop after 30s
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") stopRecording();
      }, 30000);
    } catch (e) {
      setError(t.micDenied);
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
  };

  const handleStop = async () => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    try { await audioCtxRef.current?.close(); } catch { /* noop */ }
    audioCtxRef.current = null;
    analyserRef.current = null;

    const blob = new Blob(chunksRef.current, { type: mediaRecorderRef.current?.mimeType || "audio/webm" });
    if (blob.size < 800) {
      setStage(STAGE_IDLE);
      setError(t.didntCatch);
      return;
    }

    setStage(STAGE_TRANSCRIBING);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      fd.append("space", spaceSlug);
      const { data } = await api.post(`/public/voice/transcribe?space=${encodeURIComponent(spaceSlug)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (data?.text || "").trim();
      if (!text) {
        setStage(STAGE_IDLE);
        setError(t.didntCatch);
        return;
      }
      setTranscript(text);

      // Chat
      setStage(STAGE_THINKING);
      const chat = await api.post(`/public/ai-chat?space=${encodeURIComponent(spaceSlug)}`, {
        question: text, lang, space: spaceSlug,
      });
      const reply = chat.data?.answer || "";
      setAnswer(reply);
      setSources(chat.data?.sources || []);

      // TTS
      if (ttsOn && reply && "speechSynthesis" in window) {
        speakOut(reply);
      } else {
        setStage(STAGE_IDLE);
      }
    } catch (e) {
      setStage(STAGE_IDLE);
      setError(t.didntCatch);
    }
  };

  const speakOut = (text) => {
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = lang === "ar" ? "ar-SA" : "en-US";
      utter.rate = 1.0;
      utter.pitch = 1.0;
      utter.onend = () => setStage(STAGE_IDLE);
      utter.onerror = () => setStage(STAGE_IDLE);
      setStage(STAGE_SPEAKING);
      window.speechSynthesis.speak(utter);
    } catch {
      setStage(STAGE_IDLE);
    }
  };

  const drawWaveform = () => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = canvas.clientWidth;
    const H = canvas.clientHeight;
    canvas.width = W * dpr; canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const data = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      analyser.getByteFrequencyData(data);
      ctx.clearRect(0, 0, W, H);

      const bars = 38;
      const step = Math.floor(data.length / bars);
      const barW = (W / bars) * 0.55;
      const gap = (W / bars) * 0.45;
      for (let i = 0; i < bars; i++) {
        const v = data[i * step] / 255;
        const h = Math.max(4, v * (H * 0.95));
        const x = i * (barW + gap) + gap / 2;
        const y = H / 2 - h / 2;
        const grad = ctx.createLinearGradient(0, y, 0, y + h);
        grad.addColorStop(0, primary);
        grad.addColorStop(1, hexLighten(primary, -20));
        ctx.fillStyle = grad;
        ctx.beginPath();
        roundRect(ctx, x, y, barW, h, Math.min(barW, 6) / 2);
        ctx.fill();
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  const orbClick = () => {
    if (stage === STAGE_IDLE) startRecording();
    else if (stage === STAGE_RECORDING) stopRecording();
    else if (stage === STAGE_SPEAKING) { window.speechSynthesis?.cancel(); setStage(STAGE_IDLE); }
  };

  if (!open) return null;

  const isLight = theme === "light";
  const statusText = stage === STAGE_RECORDING ? t.recording(seconds)
    : stage === STAGE_TRANSCRIBING ? t.transcribing
    : stage === STAGE_THINKING ? t.thinking
    : stage === STAGE_SPEAKING ? t.speaking
    : t.tapToSpeak;
  const hintText = stage === STAGE_RECORDING ? t.hintRecording : t.hintIdle(spaceName || "");

  // Orb gradient varies by stage
  const orbGradient = stage === STAGE_RECORDING
    ? `radial-gradient(circle at 35% 35%, #FCA5A5, #EF4444 60%, #B91C1C)`
    : stage === STAGE_THINKING || stage === STAGE_TRANSCRIBING
      ? `radial-gradient(circle at 35% 35%, #FCD34D, #F59E0B 60%, #B45309)`
      : stage === STAGE_SPEAKING
        ? `radial-gradient(circle at 35% 35%, #C4B5FD, #8B5CF6 60%, #6D28D9)`
        : `radial-gradient(circle at 35% 35%, #A5F3FC, ${primary} 60%, #0E7490)`;

  return (
    <div
      className={`fixed inset-0 ${isLight ? "bg-white text-zinc-900" : "bg-[#06080d] text-white"} flex flex-col`}
      style={{ zIndex: 999999 }}
      data-testid="voiceai-panel"
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      {/* Ambient stars in background */}
      <AIStarfield active={open} mode="ambient" color={primary} density={0.6} zIndex={0} />

      {/* Soft blobs */}
      <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-[0.18] blur-3xl"
          style={{ background: `radial-gradient(circle, ${primary}, transparent 70%)` }} />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.18] blur-3xl"
          style={{ background: `radial-gradient(circle, #8B5CF6, transparent 70%)` }} />
      </div>

      {/* Top bar */}
      <div className="relative flex items-center justify-between p-4 sm:p-6" style={{ zIndex: 2 }}>
        <button
          onClick={() => setTtsOn((v) => !v)}
          title={ttsOn ? t.speakToggleOn : t.speakToggleOff}
          data-testid="voiceai-tts-toggle"
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] border transition-colors ${
            isLight ? "border-zinc-200 hover:border-zinc-900" : "border-white/15 hover:border-white/50"
          }`}
        >
          {ttsOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
          {ttsOn ? t.speakToggleOn : t.speakToggleOff}
        </button>
        <button
          onClick={onClose}
          data-testid="voiceai-close"
          className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
            isLight ? "hover:bg-zinc-100" : "hover:bg-white/10"
          }`}
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Main content */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-4 sm:px-6 text-center" style={{ zIndex: 2 }}>
        {/* Top waveform area */}
        <div className="h-20 sm:h-24 w-full max-w-md flex items-center justify-center mb-6">
          {stage === STAGE_RECORDING ? (
            <canvas ref={canvasRef} className="w-full h-full" data-testid="voiceai-waveform" />
          ) : (
            <Sparkles className="w-10 h-10 opacity-30" />
          )}
        </div>

        {/* Title + subtitle */}
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight flex items-center gap-2 mb-3">
          {spaceName}
          <Sparkles className="w-7 h-7" style={{ color: primary }} />
        </h1>
        <div className="flex items-center justify-center gap-2 mb-12 text-sm opacity-70">
          {lang === "ar" ? (spaceName ? `مرشد ${spaceName}` : "مرشد") : (spaceName ? `${spaceName} guide` : "Guide")}
        </div>

        {/* Status text */}
        <div
          className={`text-2xl sm:text-3xl font-bold mb-6 transition-colors ${
            stage === STAGE_RECORDING ? "text-rose-400" :
            stage === STAGE_THINKING || stage === STAGE_TRANSCRIBING ? "text-amber-300" :
            stage === STAGE_SPEAKING ? "text-violet-300" :
            isLight ? "text-zinc-700" : "text-white/90"
          }`}
          data-testid="voiceai-status"
        >
          {statusText}
        </div>

        {/* The orb */}
        <button
          onClick={orbClick}
          data-testid="voiceai-orb"
          className={`relative w-44 h-44 sm:w-52 sm:h-52 rounded-full flex items-center justify-center transition-transform active:scale-95 ${
            stage === STAGE_RECORDING ? "voiceai-pulse-recording" :
            stage === STAGE_IDLE ? "voiceai-pulse-idle" :
            "voiceai-pulse-busy"
          }`}
          style={{ background: orbGradient }}
        >
          <span className="absolute inset-0 rounded-full" style={{
            boxShadow: stage === STAGE_RECORDING
              ? "0 0 80px rgba(239,68,68,0.55), inset 0 0 30px rgba(255,255,255,0.18)"
              : `0 0 80px ${primary}66, inset 0 0 30px rgba(255,255,255,0.18)`,
          }} />
          {stage === STAGE_RECORDING ? (
            <Square className="w-14 h-14 text-white drop-shadow-lg" fill="currentColor" />
          ) : stage === STAGE_TRANSCRIBING || stage === STAGE_THINKING ? (
            <span className="askai-dots text-white"><span></span><span></span><span></span></span>
          ) : stage === STAGE_SPEAKING ? (
            <Volume2 className="w-14 h-14 text-white drop-shadow-lg" />
          ) : (
            <Mic className="w-14 h-14 text-white drop-shadow-lg" />
          )}
        </button>

        {/* Hint */}
        <div className="mt-8 text-sm opacity-60 max-w-md">
          {error ? <span className="text-rose-400">{error}</span> : hintText}
        </div>
      </div>

      {/* Transcript + answer area */}
      {(transcript || answer) && (
        <div className={`relative max-w-2xl w-full mx-auto px-4 pb-6 sm:pb-10 space-y-3`} style={{ zIndex: 2 }} data-testid="voiceai-transcript">
          {transcript && (
            <div className={`px-4 py-3 rounded-2xl text-sm ${
              isLight ? "bg-zinc-100" : "bg-white/5 border border-white/10"
            }`}>
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-50 font-bold mb-1">{t.you}</div>
              <div>{transcript}</div>
            </div>
          )}
          {answer && (
            <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
              isLight ? "bg-white border border-zinc-200" : "bg-white/[0.06] border border-white/15"
            }`}>
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-50 font-bold mb-1" style={{ color: primary }}>
                {t.assistant}
              </div>
              <div className="whitespace-pre-wrap">{answer}</div>
              {sources?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <span className="text-[9px] uppercase tracking-[0.2em] opacity-50 font-bold self-center">{t.sources}</span>
                  {sources.slice(0, 4).map((s) => (
                    <Link key={s.article_id}
                      to={`/s/${spaceSlug}/articles/${s.article_slug}${lang === "ar" ? "?lang=ar" : ""}`}
                      data-testid={`voiceai-source-${s.article_slug}`}
                      className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full transition-colors ${
                        isLight ? "bg-zinc-50 border border-zinc-200 hover:border-zinc-900" : "bg-black/40 border border-white/15 hover:border-white/40"
                      }`}
                      title={s.title}>
                      <FileText className="w-2.5 h-2.5" />
                      <span className="truncate max-w-[160px]">{s.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ───── Utilities ───────────────────────────────────────────────
function hexLighten(hex, percent) {
  const h = hex.replace("#", "");
  const num = parseInt(h, 16);
  let r = (num >> 16) + Math.round((255 * percent) / 100);
  let g = ((num >> 8) & 0x00ff) + Math.round((255 * percent) / 100);
  let b = (num & 0x0000ff) + Math.round((255 * percent) / 100);
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
